import type { Template } from "@/schema/templates";

import { t } from "@lingui/core/macro";
import { Link } from "@tanstack/react-router";

export type LandingPage = {
  slug: string;
  /** Small label above the heading. */
  eyebrow: string;
  /**
   * Short, keyword-bearing label for navigation and breadcrumbs. `eyebrow` reads
   * as an audience badge ("For students"); anchor text needs the term someone
   * would actually search for.
   */
  navLabel: string;
  /**
   * Keyword-targeted `<h1>`. Kept separate from `title` so the SERP entry and
   * the on-page headline can each be tuned without dragging the other along.
   */
  heading: string;
  /** Bare `<title>`, composed into `... - ResumeLabs` by `pageTitle()`. */
  title: string;
  description: string;
  /** Lead paragraph, rendered above the fold and reused as the schema abstract. */
  intro: string;
  /**
   * Date of the last meaningful content edit - feeds sitemap `lastmod`. Bump it
   * by hand when the copy changes; never emit `new Date()` here.
   */
  updated: string;
  body: React.ReactNode;
  /** Templates worth surfacing for this audience, with the reason why. */
  templates: { template: Template; reason: string }[];
  /** Rendered as a visible FAQ and emitted as `FAQPage` JSON-LD. */
  faq: { q: string; a: string }[];
  /** Article slugs to cross-link at the foot of the page. */
  relatedArticles: string[];
};

export const getLandingPages = (): LandingPage[] => [
  {
    slug: "student",
    eyebrow: t`For students`,
    navLabel: t`Student Resume`,
    heading: t`Student Resume: How to Write One With Little Work Experience`,
    title: t`Student Resume - Templates, Examples & a Step-by-Step Guide`,
    description: t`How to write a student resume when your work history is thin: what to put first, what counts as experience, and free templates built for a single page.`,
    intro: t`Most students hit the same wall - the posting asks for years of experience and you have a degree in progress, a part-time job, and a couple of course projects. That is enough. A student resume wins on structure and specificity, not length: put education first, treat everything you have actually done as experience, and keep it to one page.`,
    updated: "2026-08-25",
    templates: [
      {
        template: "ditto",
        reason: t`Minimal and text-dense with no decoration - the safest pick when your resume goes through an automated screening system.`,
      },
      {
        template: "rhyhorn",
        reason: t`Single-column and plain, so a short history fills the page without looking padded.`,
      },
      {
        template: "chikorita",
        reason: t`A soft header accent and photo slot, for places that expect a more personal first impression.`,
      },
    ],
    faq: [
      {
        q: t`How long should a student resume be?`,
        a: t`One page. With under three years of full-time experience there is no reason to spill onto a second, and a recruiter working through a stack will read a tight single page far more carefully than a padded double.`,
      },
      {
        q: t`Should education go above or below experience?`,
        a: t`Above, while you are still studying or within about a year of graduating. Your degree is the strongest thing on the page at that point, so it should not be buried under a summer job.`,
      },
      {
        q: t`Do I include my military or national service?`,
        a: t`Yes. Service is real experience - list your role, the type of unit where you can name it, and what you were responsible for. Leadership, training others, working under pressure, and handling equipment or budgets all translate directly to civilian roles.`,
      },
      {
        q: t`What if I have never had a job at all?`,
        a: t`Then the experience section is built from course projects, volunteering, student organizations, tutoring, and freelance work - each written up with dates and outcomes exactly like a job.`,
      },
      {
        q: t`Should I include my GPA?`,
        a: t`Only if it is strong - roughly the top third of your class - or if the employer explicitly asks for it. A middling GPA on the page invites a question you would rather answer in the interview.`,
      },
    ],
    relatedArticles: ["resume-length-guide", "skills-section-guide", "resume-summary-examples"],
    body: (
      <>
        <h2>{t`Lead with education while it is your strongest asset`}</h2>
        <p>
          {t`For the first year or two of your studies, your degree is the most relevant thing you have. Put the education block directly under your contact details and summary, and give it more than one line: institution, degree, expected graduation year, and two or three specifics a recruiter would actually care about.`}
        </p>
        <ul>
          <li>{t`Relevant coursework - name the four or five courses that map to the job, not your entire transcript.`}</li>
          <li>{t`Final or capstone projects, with what you built and which tools you used.`}</li>
          <li>{t`Academic honors, scholarships, or excellence list placements.`}</li>
          <li>{t`Thesis or research topic, if you have one and it is relevant.`}</li>
        </ul>
        <p>
          {t`Once you have two or three years of full-time work behind you, flip the order and let experience lead.`}
        </p>

        <h2>{t`Everything you have done counts as experience`}</h2>
        <p>
          {t`The experience section is not reserved for salaried, full-time jobs. Anything where you took responsibility and produced a result belongs there, described the way you would describe a job - role, organization, dates, and bullets that say what changed because you were involved.`}
        </p>
        <ul>
          <li>{t`Military or national service - your role, what you were responsible for, and how many people or systems you handled.`}</li>
          <li>{t`Part-time and summer jobs, including retail and food service. They demonstrate reliability, customer handling, and working under pressure.`}</li>
          <li>{t`Teaching assistant, lab assistant, or tutoring positions.`}</li>
          <li>{t`Volunteering and student organizations, especially anything where you organized, led, or fundraised.`}</li>
          <li>{t`Freelance or side projects with real users, even unpaid ones.`}</li>
          <li>{t`Internships, however short.`}</li>
        </ul>

        <h2>{t`Write bullets that show a result, not a job description`}</h2>
        <p>
          {t`The single biggest upgrade to a student resume is rewriting duty statements as outcomes. "Responsible for the cash register" says nothing a recruiter cannot guess from the job title. Attach a number and a consequence instead.`}
        </p>
        <ul>
          <li>{t`Before: "Worked at the campus library." After: "Handled circulation for a 400-seat library, trained four new student workers, and cut the shelving backlog from two days to same-day."`}</li>
          <li>{t`Before: "Member of the robotics club." After: "Built the drive system for a competition robot that placed third out of 22 teams."`}</li>
          <li>{t`Before: "Tutored students." After: "Tutored 12 first-year students in calculus; nine improved by at least one full grade."`}</li>
        </ul>
        <p>
          {t`If you are stuck turning duties into numbers, this walks through the method:`}{" "}
          <Link to="/articles/$slug" params={{ slug: "quantifying-achievements" }}>
            {t`How to Quantify Achievements on Your Resume`}
          </Link>
        </p>

        <h2>{t`Keep the skills section honest and specific`}</h2>
        <p>
          {t`List tools and languages you could be tested on tomorrow. Drop the generic soft skills - "team player" and "hard working" appear on every resume and carry no signal. If a soft skill genuinely matters, prove it in a bullet point instead of asserting it in a list.`}
        </p>
        <p>
          {t`Do mark proficiency honestly. "Python (comfortable), SQL (basic)" reads as self-aware; claiming fluency in eight technologies reads as noise, and it falls apart in the first technical screen.`}
        </p>

        <h2>{t`Formatting rules that matter more than you think`}</h2>
        <ul>
          <li>{t`One page, always, at this stage of your career.`}</li>
          <li>{t`A standard, readable font at 10-12pt, with margins no tighter than 1.5cm.`}</li>
          <li>{t`Consistent date formatting down the whole page.`}</li>
          <li>{t`Export as PDF so your layout survives the trip to the recruiter's screen.`}</li>
          <li>{t`Name the file properly - "Dana-Levi-Resume.pdf", not "resume-final-v3.pdf".`}</li>
          <li>{t`Use a professional email address. A university address is fine, and slightly better than a personal one.`}</li>
        </ul>

        <h2>{t`Mistakes that quietly sink student resumes`}</h2>
        <ul>
          <li>{t`Padding with high school achievements once you are past first year.`}</li>
          <li>{t`Listing every course you have taken instead of the relevant handful.`}</li>
          <li>{t`Sending an identical resume to every opening rather than matching keywords to each posting.`}</li>
          <li>{t`Burying the degree at the bottom of the page.`}</li>
          <li>{t`Writing an objective statement about what you want, instead of a summary about what you offer.`}</li>
        </ul>
      </>
    ),
  },

  {
    slug: "hi-tech",
    eyebrow: t`For tech roles`,
    navLabel: t`Hi-Tech Resume`,
    heading: t`Hi-Tech Resume: Writing a CV That Gets Past the Screen`,
    title: t`Hi-Tech Resume - How to Write a CV for Tech Roles`,
    description: t`Write a hi-tech resume that survives automated screening and holds a recruiter's attention: structure, keyword matching, project sections, and ATS-safe templates.`,
    intro: t`A hi-tech resume is read twice - first by an automated system matching it against the job description, then by a person giving it a few seconds before deciding. Both readers want the same thing: concrete technologies, shipped work, and measurable impact, arranged so they can be found without hunting.`,
    updated: "2026-08-25",
    templates: [
      {
        template: "ditgar",
        reason: t`A dark sidebar with a skills grid - dense enough for a long tech stack without crowding the experience column.`,
      },
      {
        template: "ditto",
        reason: t`Text-dense and undecorated, which is what you want when the first reader is a parser rather than a person.`,
      },
      {
        template: "onyx",
        reason: t`Clean single-column layout that stays readable when the experience section runs long.`,
      },
    ],
    faq: [
      {
        q: t`Should a developer resume be one page or two?`,
        a: t`One page under roughly five years of experience, two beyond that. A senior engineer with a decade of shipped systems has legitimate material for a second page; a junior filling one is padding.`,
      },
      {
        q: t`Where do I put my tech stack?`,
        a: t`In a dedicated skills block near the top, grouped by category - languages, frameworks, infrastructure, tools. Screening systems match against this block, and a recruiter checks it before reading anything else.`,
      },
      {
        q: t`Do I need a GitHub or portfolio link?`,
        a: t`If the profile is worth reading, yes - put it in the header next to your email. If it is three abandoned tutorial repositories, leave it out. An empty profile is worse than no link at all.`,
      },
      {
        q: t`How do I handle a career gap?`,
        a: t`State it plainly with dates and a one-line reason, and mention anything you built or studied during it. An unexplained gap invites speculation; a stated one usually ends the question.`,
      },
      {
        q: t`Should I list every technology I have touched?`,
        a: t`No. List what you could discuss for ten minutes under questioning. Padding the stack gets you into interviews you then fail, which costs more than the interview you did not get.`,
      },
    ],
    relatedArticles: ["ats-resume-guide", "quantifying-achievements", "skills-section-guide"],
    body: (
      <>
        <h2>{t`Match the job description before you polish anything`}</h2>
        <p>
          {t`Most tech applications are filtered against the posting before a person sees them, and that filter is largely a keyword match. So the highest-leverage edit is not better prose - it is making sure the exact terms from the posting appear in your resume, wherever you genuinely have the experience.`}
        </p>
        <ul>
          <li>{t`Pull the named technologies, methodologies, and role title out of the posting.`}</li>
          <li>{t`Use the posting's exact spelling - "Node.js" and "NodeJS" are not always matched to each other.`}</li>
          <li>{t`Spell out acronyms once alongside the short form: "Continuous Integration (CI)".`}</li>
          <li>{t`Put the terms in context inside experience bullets, not only in the skills list.`}</li>
        </ul>
        <p>
          {t`The full mechanics of automated screening are covered here:`}{" "}
          <Link to="/articles/$slug" params={{ slug: "ats-resume-guide" }}>
            {t`How to Write an ATS-Friendly Resume`}
          </Link>
        </p>

        <h2>{t`Structure the page for a tech reader`}</h2>
        <p>
          {t`Tech recruiters scan in a predictable order. Give them the page in that order and you buy yourself several extra seconds of attention.`}
        </p>
        <ul>
          <li>{t`Header - name, role title, city, email, phone, and links to GitHub, LinkedIn, or a portfolio.`}</li>
          <li>{t`Summary - two or three lines naming your specialization, years of experience, and strongest result.`}</li>
          <li>{t`Technical skills - grouped by category so the eye can jump to the relevant group.`}</li>
          <li>{t`Experience - reverse chronological, with the stack named inside each role.`}</li>
          <li>{t`Projects - for juniors, this section can outweigh experience entirely.`}</li>
          <li>{t`Education and certifications - last, unless you have just graduated.`}</li>
        </ul>

        <h2>{t`Write experience bullets as engineering outcomes`}</h2>
        <p>
          {t`"Worked on the backend" describes a seat, not a contribution. Every bullet should name what you built, the technology you built it with, and what measurably changed - latency, cost, throughput, error rate, release frequency, or user numbers.`}
        </p>
        <ul>
          <li>{t`"Rebuilt the checkout service in Go, cutting p95 latency from 800ms to 190ms and removing a weekly on-call incident."`}</li>
          <li>{t`"Migrated 40 microservices to a shared Kubernetes platform, reducing infrastructure spend 30% year over year."`}</li>
          <li>{t`"Introduced end-to-end tests to a legacy React codebase, lifting coverage from 12% to 68% and halving regression bugs."`}</li>
          <li>{t`"Designed the event pipeline processing 4M daily records, replacing a nightly batch job with sub-minute freshness."`}</li>
        </ul>
        <p>
          {t`If you do not have exact figures, estimate honestly and say so - "roughly a third faster" is far stronger than no number at all.`}
        </p>

        <h2>{t`Projects carry junior resumes`}</h2>
        <p>
          {t`Without professional experience, a projects section is your evidence. Two or three real projects, described properly, beat a list of ten tutorial clones. For each one give the problem, the stack, your specific role if it was a team effort, and a link to running code.`}
        </p>
        <ul>
          <li>{t`Prefer projects with real users, even a handful, over toy exercises.`}</li>
          <li>{t`Say what was hard about it and how you solved it - that is what gets discussed in the interview.`}</li>
          <li>{t`Make sure every link works and every repository has a README.`}</li>
          <li>{t`Open-source contributions count, including documentation and bug fixes.`}</li>
        </ul>

        <h2>{t`Non-engineering roles in tech`}</h2>
        <p>
          {t`The same rules apply to product, QA, data, design, and support roles - only the vocabulary changes. Product managers quantify adoption and retention; QA engineers quantify escaped defects and automation coverage; data professionals name the pipeline scale and the decision their analysis changed. In every case the tools you use belong in a named skills block, exactly as a stack would.`}
        </p>

        <h2>{t`Formatting that survives a parser`}</h2>
        <ul>
          <li>{t`Export as PDF unless the posting explicitly asks for DOCX.`}</li>
          <li>{t`Keep contact details in the body of the document, never in the header or footer region - parsers routinely skip those.`}</li>
          <li>{t`Avoid tables and text boxes for content a parser has to read.`}</li>
          <li>{t`Never put text inside an image. It is invisible to every screening system.`}</li>
          <li>{t`Use standard section headings - "Experience", not "Where I've Made an Impact".`}</li>
        </ul>
      </>
    ),
  },

  {
    slug: "no-experience",
    eyebrow: t`Starting out`,
    navLabel: t`Resume With No Experience`,
    heading: t`Resume With No Experience: A Step-by-Step Guide`,
    title: t`Resume With No Experience - How to Write One That Works`,
    description: t`Writing a first resume with no work history: what belongs in each section, what counts as experience, and how to fill a page honestly without padding it.`,
    intro: t`Writing a first resume feels like being asked to prove something you have not done yet. But "no experience" almost never means no evidence - it means the evidence sits in coursework, volunteering, service, and personal projects rather than in job titles. The task is to organize what you have into the shape a recruiter expects to read.`,
    updated: "2026-08-25",
    templates: [
      {
        template: "rhyhorn",
        reason: t`Plain and single-column, which fills a page naturally when there is not much history to show.`,
      },
      {
        template: "ditto",
        reason: t`Undecorated and parser-safe, a reliable default for a first application.`,
      },
      {
        template: "kakuna",
        reason: t`Generous spacing and clear section breaks - a short resume reads as deliberate rather than sparse.`,
      },
    ],
    faq: [
      {
        q: t`What do I put in the experience section if I have never worked?`,
        a: t`Rename it. "Experience" can become "Relevant Experience" or "Projects & Volunteering", and it holds course projects, volunteer roles, student organizations, tutoring, freelance work, and military or national service - each written up with dates and outcomes exactly like a job.`,
      },
      {
        q: t`Is it acceptable to send a half-page resume?`,
        a: t`It is better than padding, but usually avoidable. Expanding education with coursework and projects, and adding a proper skills section, gets almost everyone to a full page honestly.`,
      },
      {
        q: t`Should I write an objective or a summary?`,
        a: t`A summary. An objective describes what you want; a summary describes what you bring. Even with no jobs behind you, you can state what you are trained in and what you are looking to do.`,
      },
      {
        q: t`Do I need a cover letter?`,
        a: t`With a thin resume, yes - it is where you explain motivation and context that bullet points cannot carry. It is the one place a first-time applicant can genuinely outcompete a more experienced one.`,
      },
      {
        q: t`Can I list skills I learned from online courses?`,
        a: t`Yes, provided you can actually use them. Name the course and platform, and where possible link a project you built with the skill - that turns a claim into evidence.`,
      },
    ],
    relatedArticles: ["resume-summary-examples", "skills-section-guide", "common-resume-mistakes"],
    body: (
      <>
        <h2>{t`Start by inventorying what you actually have`}</h2>
        <p>
          {t`Before writing anything, list every situation where you were responsible for something and it went well. Do not filter for relevance yet - just get it all down. Most people are surprised by the length of the list.`}
        </p>
        <ul>
          <li>{t`Course projects, final projects, and lab work.`}</li>
          <li>{t`Military or national service, in any role.`}</li>
          <li>{t`Volunteering, community work, and youth movement leadership.`}</li>
          <li>{t`Student organizations, committees, and event organizing.`}</li>
          <li>{t`Tutoring, coaching, babysitting, and informal teaching.`}</li>
          <li>{t`Family business help, however unofficial.`}</li>
          <li>{t`Personal projects - a website, an app, a photography portfolio, a managed online community.`}</li>
          <li>{t`Online courses and certifications you finished.`}</li>
          <li>{t`Languages, including the ones you grew up with.`}</li>
        </ul>

        <h2>{t`The section order that works without a job history`}</h2>
        <p>
          {t`Standard resume order assumes work experience is the centerpiece. When it is not, reorder so the strongest material sits highest on the page.`}
        </p>
        <ul>
          <li>{t`Contact details - name, city, phone, professional email, and any relevant link.`}</li>
          <li>{t`Summary - two or three lines on what you are trained in and what you are looking for.`}</li>
          <li>{t`Education - expanded, with coursework, projects, and honors.`}</li>
          <li>{t`Relevant experience - projects, volunteering, service, and any part-time work.`}</li>
          <li>{t`Skills - tools, software, and languages, honestly rated.`}</li>
          <li>{t`Additional - certifications, awards, or interests that genuinely add something.`}</li>
        </ul>

        <h2>{t`Write a summary that offers rather than asks`}</h2>
        <p>
          {t`The weakest opening line on a first resume is a wish: "Seeking an opportunity to grow and develop in a dynamic company." It describes what you want from them. Turn it around and describe what they get.`}
        </p>
        <ul>
          <li>{t`Weak: "Motivated graduate looking for my first role in marketing."`}</li>
          <li>{t`Strong: "Marketing graduate who ran a student campaign that grew event attendance 60% on a zero budget. Comfortable with analytics tools and content production, looking to move into digital marketing."`}</li>
          <li>{t`Weak: "Ambitious student seeking a challenging position in software."`}</li>
          <li>{t`Strong: "Computer science student who has shipped two React applications with live users, one of them a paid freelance project. Looking for a junior front-end role."`}</li>
        </ul>
        <p>
          {t`More worked examples by field and level:`}{" "}
          <Link to="/articles/$slug" params={{ slug: "resume-summary-examples" }}>
            {t`Resume Summary Examples: 15+ Templates by Job & Level`}
          </Link>
        </p>

        <h2>{t`Translate what you have done into workplace language`}</h2>
        <p>
          {t`Everything on your inventory list has a professional equivalent. The skill is real; only the setting was unpaid.`}
        </p>
        <ul>
          <li>{t`Organizing a student event becomes project management, budgeting, and vendor coordination.`}</li>
          <li>{t`Leading a youth group becomes team leadership, planning, and responsibility for minors.`}</li>
          <li>{t`A group course project becomes cross-functional collaboration under a deadline.`}</li>
          <li>{t`Running a community's social account becomes content strategy and audience growth.`}</li>
          <li>{t`Tutoring becomes training, explaining complex material, and tracking progress.`}</li>
        </ul>
        <p>
          {t`Write each one with the same anatomy as a job: what you were responsible for, what you did, and what the result was, with a number wherever one exists.`}
        </p>

        <h2>{t`Fill the page honestly, not with padding`}</h2>
        <p>
          {t`There is a real difference between developing thin material and inflating it. Expanding your education block with the four courses relevant to the job is development. Listing every course you have taken since first year is padding, and recruiters recognize it instantly.`}
        </p>
        <ul>
          <li>{t`Do expand: coursework, project descriptions, tools, measurable outcomes.`}</li>
          <li>{t`Do not expand: font size beyond 12pt, margins beyond 2.5cm, or filler like "references available on request".`}</li>
          <li>{t`Never invent a role, a date, or a qualification. It is the one mistake with no recovery.`}</li>
        </ul>

        <h2>{t`Tailor before every single application`}</h2>
        <p>
          {t`With a short resume, tailoring matters more, not less - there is less material for a recruiter to find relevance in, so you have to put it in front of them. Reread the posting, then reorder your bullets and skills so the ones matching the role sit highest.`}
        </p>
        <p>
          <Link to="/articles/$slug" params={{ slug: "tailoring-resume-for-each-job" }}>
            {t`How to Tailor Your Resume for Each Job`}
          </Link>
        </p>
      </>
    ),
  },
];
